import { useEffect, useState } from "react";
import { Col, FormGroup, Input, Label, Row, Table } from "reactstrap";
import Select from "react-select";
import { IoMdClose } from "react-icons/io";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import FixedSave from "../../../../components/Actions/FixedSave";
import SelectCustomStyles from "../../../../utils/styles/reactSelect";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { get, post } from "../../../../utils/helpers/api";
import { toast } from "react-toastify";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import { useTranslation } from "react-i18next";
import CustomSelect from "../../../../components/Forms/CustomSelect";
import DefaultButton from "../../../../components/Buttons/Default";
import TaxModal from "../Quotes/Modals/Tax";
import DiscountModal from "../Quotes/Modals/Discount";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns";
import { useSettings } from "../../../../context/settings";
import WarehouseSelect from "../../../../components/Forms/WarehouseSelect";

function emptyRow() {
  return {
    id: Date.now(),
    product_name: '',
    product_id: null,
    description: '',
    quantity: 1,
    unitPrice: 0,
    discountRate: 0,
    total: 0
  };
}

function lineTotal(quantity, unitPrice, discountRate) {
  return parseFloat(quantity || 0) * parseFloat(unitPrice || 0) * (1 - parseFloat(discountRate || 0) / 100);
}

function CreateDeliveryNotePage() {
  const { t } = useTranslation();
  const { datePickerFormat, updateSettings } = useSettings();
  const { state } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlCustomerId = searchParams.get('customer_id') ? searchParams.get('customer_id') : null;
  const [saveLoading, setSaveLoading] = useState(false);
  const [employeesData, setEmployeesData] = useState([]);
  const [companySettings, setCompanySettings] = useState({});
  const [taxModalOpen, setTaxModalOpen] = useState(false);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [deliveryNoteData, setDeliveryNoteData] = useState({
    customer: state?.deliveryNoteData?.customer ? { value: state.deliveryNoteData.customer_id, label: state.deliveryNoteData?.customer?.company_name } : null,
    customer_id: state?.deliveryNoteData?.customer_id || urlCustomerId || "",
    delivery_note_number: state?.deliveryNoteData?.delivery_note_number || "",
    delivery_date: state?.deliveryNoteData?.delivery_date ? parseISO(state.deliveryNoteData.delivery_date) : new Date(),
    warehouse_id: state?.deliveryNoteData?.warehouse_id || "",
    sales_person_id: state?.deliveryNoteData?.sales_person_id || null,
    delivery_address: state?.deliveryNoteData?.delivery_address || '',
    driver_name: state?.deliveryNoteData?.driver_name || '',
    vehicle_registration: state?.deliveryNoteData?.vehicle_registration || '',
    show_prices: state?.deliveryNoteData?.show_prices ?? true,
    tax_rate: state?.deliveryNoteData?.tax_rate || 0,
    discount_rate: state?.deliveryNoteData?.discount_rate || 0,
    discount_type: state?.deliveryNoteData?.discount_type || 'percent',
    notes: state?.deliveryNoteData?.notes || '',
  });
  const [rows, setRows] = useState(
    state?.deliveryNoteData?.items?.length > 0 ? state.deliveryNoteData.items.map((item, index) => ({
      id: Date.now() + index,
      product_name: item.product_name,
      product_id: item.product_id,
      description: item.description,
      quantity: item.quantity,
      unitPrice: item.unit_price,
      availableQty: 1,
      discountRate: item.discount_rate || 0,
      total: lineTotal(item.quantity, item.unit_price, item.discount_rate)
    })) : [emptyRow()]
  );

  useEffect(() => {
    initData();
  }, []);

  const initData = async () => {
    const data = await get('/sales/delivery-notes/create');

    if(data.success) {
      setEmployeesData(data.employees);
      setCompanySettings(data.company_settings);
      updateSettings(data.company_settings);
      if(deliveryNoteData?.delivery_note_number === "") {
        setDeliveryNoteData(prev => ({
          ...prev,
          delivery_note_number: data.delivery_note_number
        }));
      }
    }
  }

  const handleAddRow = () => {
    setRows([...rows, emptyRow()]);
  };

  const handleDeleteRow = (rowId) => {
    setRows(rows.filter(row => row.id !== rowId));
  };

  const updateRow = (rowId, changes) => {
    setRows(prev => prev.map(row => {
      if (row.id !== rowId) return row;
      const updated = { ...row, ...changes };
      return { ...updated, total: lineTotal(updated.quantity, updated.unitPrice, updated.discountRate) };
    }));
  };

  const saveDeliveryNote = async () => {
    setSaveLoading(true);

    const url = id ? '/sales/delivery-notes/update/' + id : '/sales/delivery-notes/store';
    const data = await post(url, {
      ...deliveryNoteData,
      delivery_date: deliveryNoteData.delivery_date ? format(deliveryNoteData.delivery_date, 'yyyy-MM-dd') : null,
      items: rows
    });

    if(data.success) {
      navigate('/sales/delivery-notes');
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setSaveLoading(false);
  }

  const calcPrices = () => {
    const taxRate = deliveryNoteData.tax_rate || 0;
    const discountRate = deliveryNoteData.discount_rate || 0;
    const subTotal = rows.reduce((acc, row) => acc + row.total, 0);
    const discountAmount = function() {
      if(deliveryNoteData.discount_type === 'percent') {
        return subTotal * discountRate / 100;
      } else {
        return discountRate;
      }
    };
    const taxAmount = (subTotal - discountAmount()) * taxRate / 100;
    const total = subTotal - discountAmount() + taxAmount;

    return {
      subTotal, 
      discountAmount: discountAmount(),
      taxAmount, 
      total 
    };
  }

  const showPrices = !!deliveryNoteData.show_prices;

  return(
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[t('sales.deliveryNotes'), id ? t('sales.editDeliveryNote') : t('sales.newDeliveryNote')]}
          onBack={() => navigate('/sales/delivery-notes')}
        />
        <div className="subheader-page-content scrollable">
          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.client')}: </Label>
              <CustomSelect
                value={deliveryNoteData.customer || null}
                onChange={selected => setDeliveryNoteData({ ...deliveryNoteData, customer: selected, customer_id: selected ? selected.value : null })}
                module="crm/customers"
                placeholder={t('sales.searchCustomer')}
                createLink="/crm/customers/create"
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.deliveryNoteNumber')}</Label>
              <Input
                type="text"
                value={deliveryNoteData.delivery_note_number}
                readOnly
              />
            </Col>

            <Col md="3" className="mb-3">
              <Label>{t('sales.deliveryDate')}</Label>
              <DatePicker
                selected={deliveryNoteData.delivery_date}
                onChange={(d) => setDeliveryNoteData({ ...deliveryNoteData, delivery_date: d })}
                className="form-control"
                dateFormat={datePickerFormat}
              />
            </Col>

            <Col md="3" className="mb-3">
              {/* Which warehouse the goods leave; defaults to the branch's main. */}
              <Label>{t('warehouses.shipFrom')}</Label>
              <WarehouseSelect
                value={deliveryNoteData.warehouse_id}
                onChange={(v) => setDeliveryNoteData({ ...deliveryNoteData, warehouse_id: v })}
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.salesperson')}: </Label>
              <Select
                options={employeesData.map(employee => ({ value: employee.id, label: employee.firstname }))}
                styles={SelectCustomStyles}
                value={employeesData.map(e => ({ value: e.id, label: e.firstname })).find(o => o.value === deliveryNoteData.sales_person_id) || null}
                onChange={selected => setDeliveryNoteData({ ...deliveryNoteData, sales_person_id: selected ? selected.value : null })}
              />
            </Col>

            <Col md="6" className="mb-3">
              <Label>{t('sales.deliveryAddress')}</Label>
              <Input
                type="textarea"
                rows="1"
                value={deliveryNoteData.delivery_address}
                onChange={e => setDeliveryNoteData({ ...deliveryNoteData, delivery_address: e.target.value })}
              />
            </Col>
          </Row>

          <Row>
            <Col md="3" className="mb-3">
              <Label>{t('sales.driverName')}</Label>
              <Input
                type="text"
                value={deliveryNoteData.driver_name}
                onChange={e => setDeliveryNoteData({ ...deliveryNoteData, driver_name: e.target.value })}
              />
            </Col>

            <Col md="3" className="mb-3">
              <Label>{t('sales.vehicleRegistration')}</Label>
              <Input
                type="text"
                value={deliveryNoteData.vehicle_registration}
                onChange={e => setDeliveryNoteData({ ...deliveryNoteData, vehicle_registration: e.target.value })}
              />
            </Col>

            <Col md="6" className="mb-3 d-flex align-items-end">
              <div className="d-flex align-items-center pb-2">
                <Input
                  type="checkbox"
                  className="mt-0"
                  checked={showPrices}
                  onChange={e => setDeliveryNoteData({ ...deliveryNoteData, show_prices: e.target.checked })}
                />
                <Label className="ms-2 mb-0">
                  <strong>{t('sales.showPrices')}</strong>
                  <small className="text-muted d-block">{t('sales.showPricesHint')}</small>
                </Label>
              </div>
            </Col>
          </Row>

          <Table bordered>
            <thead>
              <tr>
                <th>{t('common.item')}</th>
                <th>{t('common.description')}</th>
                <th width="100px">{t('common.quantity')}</th>
                {showPrices &&
                  <>
                    <th width="100px">{t('common.unitPrice')}</th>
                    <th width="120px">{t('sales.discountPercent')}</th>
                    <th width="140px">{t('common.total')}</th>
                  </>
                }
                <th width="40px"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((data) => (
                <tr key={data.id}>
                  <td style={{minWidth: 200}}>
                    <CustomSelect
                      value={data.product_name ? { value: data.product_id, label: data.product_name } : null}
                      onChange={(selected, objectData) => {
                        updateRow(data.id, {
                          product_id: selected ? selected.value : null,
                          product_name: selected ? selected.label : '',
                          unitPrice: objectData?.selling_price ?? data.unitPrice,
                          availableQty: objectData?.current_stock
                        });
                      }}
                      module="inventory/products"
                      placeholder={t('inventory.searchProduct')}
                      createLink="/inventory/products/create"
                      canCreate
                    />
                    {/* {data.availableQty !== undefined && data.availableQty < data.quantity &&
                      <small className="text-danger d-block">
                        {t('sales.qtyError', { qty: data.availableQty })}
                      </small>
                    } */}
                  </td>
                  <td>
                    <Input
                      type="textarea"
                      rows="1"
                      value={data.description}
                      onChange={e => updateRow(data.id, { description: e.target.value })}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={data.quantity}
                      onChange={e => updateRow(data.id, { quantity: e.target.value })}
                    />
                  </td>
                  {showPrices &&
                    <>
                      <td>
                        <Input
                          type="number"
                          value={data.unitPrice}
                          onChange={e => updateRow(data.id, { unitPrice: e.target.value })}
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          value={data.discountRate || 0}
                          onChange={e => updateRow(data.id, { discountRate: e.target.value })}
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          value={data.total}
                          readOnly
                        />
                      </td>
                    </>
                  }
                  <td>
                    <IoMdClose onClick={() => handleDeleteRow(data.id)} style={{cursor: 'pointer'}} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Row>
            <Col lg="7" md="6">
              <Row className="mb-3 g-2">
                <Col md="4">
                  <DefaultButton
                    color="primary"
                    outline size="sm"
                    onClick={handleAddRow}
                    block
                  >
                    + {t('common.addNewRow')}
                  </DefaultButton>
                </Col>
                {showPrices &&
                  <>
                    <Col md="4">
                      <DefaultButton
                        color="success"
                        outline
                        size="sm"
                        block
                        onClick={() => setDiscountModalOpen(true)}
                      >
                        + {t('sales.addDiscount')}
                      </DefaultButton>
                    </Col>
                    <Col md="4">
                      <DefaultButton
                        color="light"
                        outline
                        size="sm"
                        block
                        onClick={() => setTaxModalOpen(true)}
                      >
                        + {t('sales.editTax')}
                      </DefaultButton>
                    </Col>
                  </>
                }
              </Row>

              <FormGroup>
                <Label>{t('sales.notes')}</Label>
                <Input
                  type="textarea"
                  value={deliveryNoteData.notes}
                  onChange={e => setDeliveryNoteData({ ...deliveryNoteData, notes: e.target.value })}
                />
              </FormGroup>
            </Col>

            {showPrices &&
              <Col lg="5" md="6">
                <div className="bg-white p-3">
                  <Row className="mb-2">
                    <Col md="6">
                      <strong>{t('common.subTotal')}: </strong>
                    </Col>
                    <Col md="6" className="text-end">
                      { calcPrices().subTotal.toFixed(2) } {' '}
                      { companySettings?.sales?.base_currency }
                    </Col>
                  </Row>

                  {deliveryNoteData.discount_rate > 0 &&
                    <Row className="mb-2 text-danger">
                      <Col md="6">
                        <strong>
                          {t('sales.discountWithRate')}: 
                          ({ deliveryNoteData.discount_rate }
                          { deliveryNoteData?.discount_type === 'percent' ? '%' : '' })
                        </strong>
                      </Col>
                      <Col md="6" className="text-end">
                        -{ calcPrices().discountAmount.toFixed(2) } {' '}
                        { companySettings?.sales?.base_currency }
                      </Col>
                    </Row>
                  }

                  <Row className="mb-3">
                    <Col md="6">
                      <strong>{t('sales.taxWithRate', { rate: deliveryNoteData.tax_rate })}: </strong>
                    </Col>
                    <Col md="6" className="text-end">
                      { calcPrices().taxAmount.toFixed(2) } {' '}
                      { companySettings?.sales?.base_currency }
                    </Col>
                  </Row>

                  <hr />

                  <Row>
                    <Col md="6">
                      <h5 className="fw-bold mb-0">{t('common.total')}: </h5>
                    </Col>
                    <Col md="6" className="text-end">
                      { calcPrices().total.toFixed(2) } {' '}
                      { companySettings?.sales?.base_currency }
                    </Col>
                  </Row>
                </div>
              </Col>
            }
          </Row>
        </div>
      </div>

      <TaxModal
        isOpen={taxModalOpen}
        toggle={() => setTaxModalOpen(false)}
        taxRate={deliveryNoteData.tax_rate}
        onTaxRateChange={(newTaxRate) => setDeliveryNoteData(prev => ({ ...prev, tax_rate: newTaxRate }))}
      />

      <DiscountModal
        isOpen={discountModalOpen}
        toggle={() => setDiscountModalOpen(false)}
        discountRate={deliveryNoteData.discount_rate}
        onDiscountRateChange={(newDiscountRate) => setDeliveryNoteData(prev => ({ ...prev, discount_rate: newDiscountRate }))}
        discountType={deliveryNoteData.discount_type}
        onDiscountTypeChange={(newDiscountType) => setDeliveryNoteData(prev => ({ ...prev, discount_type: newDiscountType }))}
      />

      <FixedSave
        onSave={saveDeliveryNote}
        loading={saveLoading}
        disabled={saveLoading || !deliveryNoteData?.customer_id}
      />
    </SidebarLayout>
  );
}

export default CreateDeliveryNotePage;
